import React, { useState, useRef, useEffect } from 'react';
import { Button } from 'reactstrap';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import DeleteIcon from '@mui/icons-material/Delete';
import AddAPhotoIcon from '@mui/icons-material/AddAPhoto';

const compressImage = (file, maxWidth = 1024, quality = 0.7) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = (event) => {
            const img = new Image();
            img.src = event.target.result;
            img.onload = () => {
                const canvas = document.createElement('canvas');
                let width = img.width;
                let height = img.height;

                if (width > height) {
                    if (width > maxWidth) {
                        height = Math.round(height * (maxWidth / width));
                        width = maxWidth;
                    }
                } else {
                    if (height > maxWidth) {
                        width = Math.round(width * (maxWidth / height));
                        height = maxWidth;
                    }
                }

                canvas.width = width;
                canvas.height = height;
                const ctx = canvas.getContext('2d');
                ctx.drawImage(img, 0, 0, width, height);

                canvas.toBlob((blob) => {
                    const newFile = new File([blob], file.name, {
                        type: 'image/jpeg',
                        lastModified: Date.now()
                    });
                    resolve(newFile);
                }, 'image/jpeg', quality);
            };
            img.onerror = (err) => reject(err);
        };
        reader.onerror = (error) => reject(error);
    });
};

const MobileCameraCapture = ({ onPhotoCapture, label = "Take Photo", maxPhotos = 10 }) => {
    const inputRef = useRef(null);
    const [photos, setPhotos] = useState([]);

    const handleFileChange = async (event) => {
        const files = Array.from(event.target.files);
        if (files.length > 0) {
            const processedFiles = await Promise.all(files.map(async (file) => {
                try {
                    if (file.type.startsWith('image/')) {
                        return await compressImage(file, 1024, 0.7);
                    }
                    return file;
                } catch (e) {
                    console.error("Image compression failed", e);
                    return file; // fallback
                }
            }));

            const addedPhotos = processedFiles.map(file => ({
                file,
                url: URL.createObjectURL(file)
            }));

            // Limit total photos to maxPhotos
            const totalPhotos = [...photos, ...addedPhotos].slice(0, maxPhotos);

            setPhotos(totalPhotos);
            if (onPhotoCapture) {
                onPhotoCapture(totalPhotos.map(p => p.file));
            }
            event.target.value = '';
        }
    };

    const triggerCamera = () => {
        inputRef.current.click();
    };

    const removePhoto = (index) => {
        const newPhotos = photos.filter((_, i) => i !== index);
        setPhotos(newPhotos);
        if (onPhotoCapture) {
            onPhotoCapture(newPhotos.map(p => p.file));
        }
    };

    useEffect(() => {
        // Cleanup URLs on unmount
        return () => {
            photos.forEach(p => URL.revokeObjectURL(p.url));
        };
    }, []);

    return (
        <div className="p-3 border rounded bg-light">
            <input
                type="file"
                accept="image/*"
                capture="environment"
                multiple
                ref={inputRef}
                style={{ display: 'none' }}
                onChange={handleFileChange}
            />

            <div className="d-flex flex-wrap gap-3">
                {photos.map((photo, index) => (
                    <div key={index} className="position-relative" style={{ width: '100px', height: '100px' }}>
                        <img
                            src={photo.url}
                            alt={`Proof ${index + 1}`}
                            className="img-fluid rounded shadow-sm border"
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                        <Button
                            color="danger"
                            size="sm"
                            className="position-absolute top-0 end-0 m-1 rounded-circle p-0 d-flex align-items-center justify-content-center"
                            onClick={() => removePhoto(index)}
                            style={{ width: '20px', height: '20px' }}
                        >
                            <DeleteIcon style={{ fontSize: '0.8rem' }} />
                        </Button>
                    </div>
                ))}

                {photos.length < maxPhotos && (
                    <div
                        onClick={triggerCamera}
                        style={{
                            width: '100px',
                            height: '100px',
                            borderRadius: '12px',
                            backgroundColor: '#e9ecef',
                            border: '2px dashed #adb5bd',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            transition: 'all 0.2s'
                        }}
                    >
                        <CameraAltIcon style={{ fontSize: '2rem', color: '#6c757d' }} />
                        <span className="mt-1 text-muted small fw-bold" style={{ fontSize: '0.7em' }}>
                            {photos.length === 0 ? "Take Photo" : "+ Take"}
                        </span>
                    </div>
                )}
            </div>
            {photos.length > 0 && (
                <div className="mt-2 text-muted small text-end">
                    {photos.length}/{maxPhotos} Photos
                </div>
            )}
        </div>
    );
};

export default MobileCameraCapture;
